import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { useEffect, useId, useState } from "react";
import type { ReasonObject } from "@/libs/generated/types";

export type RejectionReasonOption = {
  value: string;
  label: string;
};

type RejectionReasonFieldsProps = {
  reason: string;
  onReasonChange: (value: string) => void;
  additionalInfo: string;
  onAdditionalInfoChange: (value: string) => void;
  reasonLabel: string;
  reasonOptions: readonly RejectionReasonOption[];
  additionalInfoLabel: string;
};

/** Reason select + optional note, shared by inline and modal rejection flows. */
export function RejectionReasonFields({
  reason,
  onReasonChange,
  additionalInfo,
  onAdditionalInfoChange,
  reasonLabel,
  reasonOptions,
  additionalInfoLabel,
}: RejectionReasonFieldsProps) {
  return (
    <Stack spacing={1.5}>
      <TextField
        select
        required
        fullWidth
        size="small"
        label={reasonLabel}
        value={reason}
        onChange={(event) => onReasonChange(event.target.value)}
      >
        {reasonOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        fullWidth
        multiline
        rows={2}
        size="small"
        label={additionalInfoLabel}
        value={additionalInfo}
        onChange={(event) => onAdditionalInfoChange(event.target.value)}
      />
    </Stack>
  );
}

export type RejectionModalProps = {
  open: boolean;
  title: string;
  description?: string;
  reasonLabel: string;
  reasonOptions: readonly RejectionReasonOption[];
  additionalInfoLabel: string;
  confirmLabel?: string;
  cancelLabel?: string;
  loading?: boolean;
  onClose: () => void;
  onConfirm: (reason: ReasonObject) => void;
};

/** Generic rejection dialog: any caller can provide its own reason options and labels. */
export function RejectionModal({
  open,
  title,
  description,
  reasonLabel,
  reasonOptions,
  additionalInfoLabel,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  loading = false,
  onClose,
  onConfirm,
}: RejectionModalProps) {
  const titleId = useId();
  const [reason, setReason] = useState("");
  const [additionalInfo, setAdditionalInfo] = useState("");

  useEffect(() => {
    if (!open) {
      setReason("");
      setAdditionalInfo("");
    }
  }, [open]);

  const close = () => {
    if (loading) return;
    setReason("");
    setAdditionalInfo("");
    onClose();
  };

  const confirm = () => {
    if (loading || !reason) return;
    onConfirm({ reason, additionalInfo: additionalInfo.trim() || undefined });
    setReason("");
    setAdditionalInfo("");
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm" aria-labelledby={titleId}>
      <DialogTitle id={titleId}>{title}</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
        {description && <Alert severity="warning">{description}</Alert>}
        <RejectionReasonFields
          reason={reason}
          onReasonChange={setReason}
          additionalInfo={additionalInfo}
          onAdditionalInfoChange={setAdditionalInfo}
          reasonLabel={reasonLabel}
          reasonOptions={reasonOptions}
          additionalInfoLabel={additionalInfoLabel}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={loading}>
          {cancelLabel}
        </Button>
        <Button color="error" variant="contained" onClick={confirm} disabled={loading || !reason}>
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export type RejectionDialogButtonProps = Omit<
  RejectionModalProps,
  "open" | "onClose" | "onConfirm"
> & {
  buttonText: string;
  buttonColor?: string;
  icon?: React.ElementType;
  disabled?: boolean;
  fullWidth?: boolean;
  onConfirm: (reason: ReasonObject) => void;
};

/** Button that opens the generic rejection dialog and returns the chosen reason. */
export function RejectionDialogButton({
  buttonText,
  buttonColor,
  icon,
  disabled = false,
  fullWidth = true,
  onConfirm,
  ...modalProps
}: RejectionDialogButtonProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        fullWidth={fullWidth}
        color="error"
        disabled={disabled || modalProps.loading}
        variant="contained"
        size="medium"
        startIcon={icon ? <Box component={icon} sx={{ fontSize: 20 }} /> : undefined}
        onClick={() => setOpen(true)}
        sx={
          buttonColor
            ? {
                backgroundColor: buttonColor,
                "&:hover": {
                  backgroundColor: buttonColor,
                  filter: "brightness(0.9)",
                },
              }
            : undefined
        }
      >
        {buttonText}
      </Button>
      <RejectionModal
        {...modalProps}
        open={open}
        onClose={() => setOpen(false)}
        onConfirm={(reason) => {
          setOpen(false);
          onConfirm(reason);
        }}
      />
    </>
  );
}
